import { Box, VBox } from "@ui/box/Box";
import { Typo } from "@ui/text/Typo";
import { ActionButton, DS } from "./ds/Kit";
import {
  Inner,
  isNavAllowed,
  useCurrentPath,
  useRouter,
} from "src/services/Router";
import { LeftMenu } from "./LeftMenu";
import { ConfirmDialog } from "./popup/general/ConfirmDialog";
import { GD } from "src/GD";
import { useEffect, useRef, useState } from "react";
import { AuthView } from "./Auth/AuthView";
import { AUTH_STATE, ILoginCompany, TUser } from "src/services/AuthService";
import { Logo } from "./assets/Logo";

import { Header } from "./Header";
import { SelectProjectView } from "./SelectProjectView";
import { PrivateView } from "./private/PrivateView";
import { isPublicAuthPath } from "src/utils/authRoutes";
import { IssuerProvider, useIssuer } from "./issuer/IssuerProvider";
import { UniversalDialog } from "./components/universalDialog/UniversalDialog";
import { IssuerReviewRestore } from "./components/IssuerReviewWait";
import { MOBILE_VIEWPORT_MEDIA as MOBILE } from "@ui/utils/mobileViewport";
import { OnboardingGate } from "./private/OnboardingGate";
import { PrivateAccountContext } from "./private/PrivateAccountContext";

export const GlobalView = () => {
  const state = GD.S_AUTH_STATUS.use([]);
  const route = useCurrentPath();
  const pageScrollRef = useRef<HTMLDivElement>(null);
  const previousAuthState = useRef(state);
  const authGeneration = useRef(0);
  if (previousAuthState.current !== state) {
    previousAuthState.current = state;
    authGeneration.current += 1;
  }
  const currentAuthGeneration = authGeneration.current;
  const [sessionContext, setSessionContext] = useState<{
    generation: number;
    company: ILoginCompany | null;
    profile: TUser | null;
    onboarding: IOnboarding | null;
  }>({ generation: -1, company: null, profile: null, onboarding: null });
  const sessionContextLoading =
    sessionContext.generation !== currentAuthGeneration;
  const company = sessionContextLoading ? null : sessionContext.company;
  const profile = sessionContextLoading ? null : sessionContext.profile;

  // Re-read the company whenever auth state changes — useRequest only fires
  // once on mount, so on first login (GlobalView already mounted) it would
  // otherwise keep the stale null and render the classic UI.
  useEffect(() => {
    let active = true;
    Promise.all([GD.REQ_COMPANY_REFRESH.request(), GD.REQ_PROFILE.request(), GD.REQ_ONBOARDING.request()])
      .then(([nextCompany, nextProfile, nextOnboarding]) => {
        if (!active) return;
        setSessionContext({
          generation: currentAuthGeneration,
          company: nextCompany ?? null,
          profile: nextProfile ?? null,
          onboarding: nextOnboarding ?? null,
        });
      })
      .catch(() => {
        if (!active) return;
        setSessionContext({
          generation: currentAuthGeneration,
          company: null,
          profile: null,
          onboarding: null,
        });
      });
    return () => {
      active = false;
    };
  }, [currentAuthGeneration, state]);

  useEffect(() => {
    GD.S_PROJECT_SELECTED.subscribe(() => {});
  }, []);

  useEffect(() => {
    const viewport = pageScrollRef.current;
    if (!viewport) return;
    viewport.scrollTop = 0;
    viewport.scrollLeft = 0;
  }, [route?.currentPath]);

  if (isPublicAuthPath(window.location.pathname)) {
    return <AuthView />;
  }

  if (state === AUTH_STATE.UNAUTHORIZED) {
    return <AuthView />;
  }

  if (state === AUTH_STATE.SELECT_PROJECT) {
    return <SelectProjectView />;
  }

  // Same loader as index.html, so startup never flashes an empty screen.
  if (state === null || sessionContextLoading) {
    return <div className="boot-loader" role="status">Loading…</div>;
  }

  // Private companies get a completely separate, slimmed-down interface.
  if (company?.type === "private") {
    return (
      <IssuerProvider
        key={`${company.uid}:${currentAuthGeneration}`}
        companyUid={company.uid}
        sessionKey={currentAuthGeneration}
      >
        <PrivateAccountContext.Provider value>
          <PrivateView />
          <IssuerReviewRestore />
          <UniversalDialog />
          <ConfirmDialog />
        </PrivateAccountContext.Provider>
      </IssuerProvider>
    );
  }

  if (
    company?.type === "company" &&
    profile?.company_role === "admin" &&
    sessionContext.onboarding?.required
  ) {
    return (
      <IssuerProvider
        key={`${company.uid}:${currentAuthGeneration}:onboarding`}
        companyUid={company.uid}
        sessionKey={currentAuthGeneration}
      >
        <OnboardingGate
          mode="company-admin"
          onApproved={() => {
            void GD.REQ_ONBOARDING_SET_STATUS.request("approved");
            setSessionContext(current => ({
              ...current,
              onboarding: current.onboarding
                ? { ...current.onboarding, required: false, kyc_status: "approved" }
                : null,
            }));
          }}
        />
        <UniversalDialog />
        <ConfirmDialog />
      </IssuerProvider>
    );
  }

  return (
    <IssuerProvider
      key={`${company?.uid ?? "company"}:${currentAuthGeneration}`}
      companyUid={company?.uid ?? null}
      sessionKey={currentAuthGeneration}
    >
    <Box data-workspace="company"
      sx={{
        display: "grid",
        gridTemplateColumns: "240px minmax(0, 1fr)",
        gridTemplateAreas: '"navigation main"',
        width: "100%",
        height: "100dvh",
        overflow: "hidden",
        backgroundColor: "#F5F6FA",
        boxSizing: "border-box",
        padding:
          "var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) 0 0",
        gap: "0",
        [MOBILE]: {
          gridTemplateColumns: "minmax(0, 1fr)",
          gridTemplateRows: "minmax(0, 1fr)",
          gridTemplateAreas: '"main"',
          padding: "0",
          gap: "0",
        },
      }}
    >
      <VBox
        gap="20px"
        sx={{
          gridArea: "navigation",
          padding: "28px 16px 18px",
          position: "relative",
          top: "0",
          height: "100%",
          overflow: "hidden",
          backgroundColor: "#151D30",
          border: "0",
          borderRadius: "0",
          color: "#FFFFFF",
          boxShadow: "none",
          [MOBILE]: {
            // Floating glass dock: page content scrolls underneath and shows through the blur.
            position: "fixed",
            left: "12px",
            right: "12px",
            top: "auto",
            bottom: "calc(10px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))",
            backgroundColor: "rgba(255, 255, 255, 0.72)",
            backdropFilter: "saturate(1.8) blur(20px)",
            WebkitBackdropFilter: "saturate(1.8) blur(20px)",
            height: "auto",
            minHeight: "0",
            margin: "0",
            padding: "6px",
            borderRadius: "22px",
            border: "1px solid rgba(15, 23, 42, 0.06)",
            boxShadow: "0 12px 32px rgba(15, 23, 42, 0.14), 0 2px 6px rgba(15, 23, 42, 0.06)",
            zIndex: 20,
          },
        }}
      >
        <Box
          sx={{
            padding: "4px 8px 10px",
            [MOBILE]: { display: "none" },
          }}
        >
          <Logo color="#FFFFFF" />
        </Box>
        <LeftMenu />
      </VBox>

      <VBox
        sx={{
          gridArea: "main",
          padding: "0px",
          paddingLeft: "0px",
          minWidth: 0,
          height: "100%",
          backgroundColor: "#F5F6FA",
          border: "0",
          borderRadius: "0",
          boxShadow: "none",
          overflow: "hidden",
          [MOBILE]: {
            height: "100%",
            minHeight: 0,
            borderRadius: "0",
            border: "0",
          },
        }}
      >
        <Header />

        <VBox
          ref={pageScrollRef}
          data-page-scroll-container
          sx={{
            flexGrow: "1",
            overflow: "auto",
            position: "relative",
            alignItems: "center",
            minHeight: 0,
            WebkitOverflowScrolling: "touch",
            overscrollBehaviorY: "contain",
            "&>div": {
              position: "absolute",
              width: "100%",
              height: "100%",
              boxSizing: "border-box",
              paddingBottom: "var(--dock-space, 0px)",
            },
            // Room for the floating dock so the last content can scroll clear of it.
            [MOBILE]: {
              ["--dock-space" as string]: "calc(90px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))",
            },
          }}
        >
          <IssuerScopedInner
            role={profile?.company_role}
            loading={sessionContextLoading}
          />
        </VBox>
        {/**/}
      </VBox>

    </Box>
    <UniversalDialog />
    <IssuerReviewRestore />
    <ConfirmDialog />
    </IssuerProvider>
  );
};

const IssuerScopedInner = ({ role, loading }: { role?: string; loading: boolean }) => {
  const { scopeKey } = useIssuer();
  return (
    <div key={scopeKey} style={{ maxWidth: "1440px", margin: "0 auto" }}>
      <AccessControlledInner role={role} loading={loading} />
    </div>
  );
};

export const AccessControlledInner = ({
  role,
  loading = false,
}: {
  role?: string;
  loading?: boolean;
}) => {
  const current = useCurrentPath();
  const router = useRouter();
  const resolved = Boolean(current?.currentNav);
  const allowed = isNavAllowed(current?.currentNav, role);

  useEffect(() => {
    if (!loading && resolved && !allowed && router)
      router.navigate("dashboard");
  }, [allowed, loading, resolved, router]);

  if (!loading && !resolved) return <RouteNotFound onHome={() => router?.navigate("dashboard")} />;
  if (loading || !resolved || !allowed) return null;
  return <Inner />;
};

const RouteNotFound = ({ onHome }: { onHome: () => void }) => (
  <VBox gap="10px" sx={{ alignItems: "center", textAlign: "center", padding: "96px 24px" }}>
    <Box sx={{ fontSize: "56px", fontWeight: 800, lineHeight: 1, letterSpacing: "-0.04em", background: DS.gradient, WebkitBackgroundClip: "text", color: "transparent" }}>404</Box>
    <Typo sx={{ fontSize: "20px", fontWeight: 700, color: DS.ink }}>This page doesn't exist</Typo>
    <Typo sx={{ fontSize: "14px", color: DS.muted, maxWidth: "380px", lineHeight: 1.5 }}>
      The link may be outdated or mistyped. Use the menu, or go back to your dashboard.
    </Typo>
    <Box sx={{ marginTop: "8px" }}>
      <ActionButton tone="primary" onClick={onHome}>Go to dashboard</ActionButton>
    </Box>
  </VBox>
);

// const Profile = () => {
// 	const [user] = GD.REQ_PROFILE.useRequest();
// 	const [project] = GD.REQ_CURRENT_PROJECT.useRequest();

// 	if (!user || !project) return null;
// 	return (
// 		<HBox center gap="6px" sx={{backgroundColor:'#557fcc22', padding:'6px', paddingRight:'12px', borderRadius:'8px'}}>
// 			<Avatar />
// 			<VBox>
// 				<Typo variant="medium"> {user.name}</Typo>
// 				<Typo variant="small">
// 					{" "}
// 					{user.projects
// 						.find(val => val.id === project.id)
// 						?.roles.map(val => val.name)
// 						.join(", ")}
// 				</Typo>
// 			</VBox>
// 		</HBox>
// 	);
// };
